import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Check, Building2, Landmark, ClipboardList, Info } from 'lucide-react';
import type { TipoContratoOperacional, TipoMedicao } from '@/hooks/useEmpContratos';
import { cn } from '@/lib/utils';

interface WizardResult {
  tipoContratoOperacional: TipoContratoOperacional;
  tipoMedicao: TipoMedicao;
  granularidade: string;
}

interface EmpContratoWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (result: WizardResult) => void;
}

const TIPOS_CONTRATO: Array<{
  value: TipoContratoOperacional;
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    value: 'preco_unitario',
    label: 'Preço Unitário (PPU)',
    description: 'Pagamento proporcional às quantidades executadas de cada item da planilha orçamentária.',
    icon: <Building2 className="h-6 w-6" />,
  },
  {
    value: 'preco_global',
    label: 'Preço Global (EAP)',
    description: 'Pagamento por marcos, eventos ou percentual de conclusão de atividades do cronograma.',
    icon: <Landmark className="h-6 w-6" />,
  },
  {
    value: 'ordem_servico',
    label: 'Ordem de Serviço (UBS)',
    description: 'Pagamento por unidade básica de serviço, com composição de insumos detalhada.',
    icon: <ClipboardList className="h-6 w-6" />,
  },
];

const METODOS_MEDICAO: Record<TipoContratoOperacional, Array<{ value: TipoMedicao; label: string; description: string }>> = {
  preco_unitario: [
    { value: 'preco_unitario', label: 'Quantitativo', description: 'Medição por quantidade executada × preço unitário (EV = Σ Qtd × PU).' },
    { value: 'hibrido', label: 'Híbrido', description: 'Permite definir método de medição individualmente por item da planilha (PPU, EAP, UBS).' },
  ],
  preco_global: [
    { value: 'epc', label: 'Marcos / Eventos', description: 'Medição por marcos concluídos ou percentual de conclusão. Todos os itens usam marco ou percentual.' },
    { value: 'hibrido', label: 'Híbrido', description: 'Combina itens quantitativos com marcos/percentual. Cada atividade define seu método individualmente.' },
  ],
  ordem_servico: [
    { value: 'preco_unitario', label: 'Quantitativo (UBS)', description: 'Medição pela quantidade de UBS executadas × preço unitário do serviço.' },
    { value: 'hibrido', label: 'Híbrido', description: 'Permite misturar OS por PU e OS por EAP no mesmo contrato.' },
  ],
};

const GRANULARIDADE: Record<TipoMedicao, { label: string; description: string }> = {
  preco_unitario: { label: 'Contínua', description: 'Progresso medido de forma contínua por quantidades parciais.' },
  epc: { label: 'Discreta', description: 'Progresso medido por marcos discretos (concluído ou não concluído).' },
  hibrido: { label: 'Mista', description: 'Combinação de medição contínua (quantidades) e discreta (marcos).' },
};

const STEPS = ['Tipo de Contrato', 'Método de Medição', 'Granularidade', 'Confirmação'];

export const EmpContratoWizard: React.FC<EmpContratoWizardProps> = ({
  open,
  onOpenChange,
  onComplete,
}) => {
  const [step, setStep] = useState(0);
  const [tipoContrato, setTipoContrato] = useState<TipoContratoOperacional | null>(null);
  const [tipoMedicao, setTipoMedicao] = useState<TipoMedicao | null>(null);

  const metodos = tipoContrato ? METODOS_MEDICAO[tipoContrato] : [];
  const granularidade = tipoMedicao ? GRANULARIDADE[tipoMedicao] : null;

  // Auto-select if only one method available
  const autoSelectedMedicao = useMemo(() => {
    if (metodos.length === 1) return metodos[0].value;
    return null;
  }, [metodos]);

  const effectiveMedicao = tipoMedicao ?? autoSelectedMedicao;

  const canNext = () => {
    if (step === 0) return !!tipoContrato;
    if (step === 1) return !!effectiveMedicao;
    if (step === 2) return !!granularidade;
    return true;
  };

  const handleNext = () => {
    if (step === 0 && autoSelectedMedicao) {
      // Skip method step if auto-selected, jump to step 2
      setTipoMedicao(autoSelectedMedicao);
      setStep(2);
      return;
    }
    if (step === 1 && effectiveMedicao) {
      setTipoMedicao(effectiveMedicao);
    }
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    }
  };

  const handleBack = () => {
    if (step === 2 && autoSelectedMedicao) {
      // Jump back to step 0 if method was auto-selected
      setStep(0);
      return;
    }
    if (step > 0) setStep(s => s - 1);
  };

  const handleConfirm = () => {
    if (tipoContrato && effectiveMedicao && granularidade) {
      onComplete({
        tipoContratoOperacional: tipoContrato,
        tipoMedicao: effectiveMedicao,
        granularidade: granularidade.label,
      });
      // Reset wizard
      setStep(0);
      setTipoContrato(null);
      setTipoMedicao(null);
      onOpenChange(false);
    }
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setStep(0);
      setTipoContrato(null);
      setTipoMedicao(null);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg">Assistente de Contrato</DialogTitle>
          {/* Step indicator */}
          <div className="flex items-center gap-1 mt-3">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <div className={cn(
                  'flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full transition-colors',
                  i === step ? 'bg-primary text-primary-foreground' :
                  i < step ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                )}>
                  <span className="w-4 h-4 rounded-full flex items-center justify-center text-xs bg-background/30">
                    {i < step ? <Check className="h-3 w-3" /> : i + 1}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                </div>
                {i < STEPS.length - 1 && <div className="h-px w-4 bg-border" />}
              </React.Fragment>
            ))}
          </div>
        </DialogHeader>

        <div className="py-4 min-h-[200px]">
          {/* Step 0: Tipo de Contrato */}
          {step === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Selecione o tipo de contrato operacional:</p>
              {TIPOS_CONTRATO.map(tipo => (
                <Card
                  key={tipo.value}
                  className={cn(
                    'cursor-pointer transition-all hover:border-primary/50',
                    tipoContrato === tipo.value && 'border-primary ring-1 ring-primary/30 bg-primary/5'
                  )}
                  onClick={() => {
                    setTipoContrato(tipo.value);
                    setTipoMedicao(null);
                  }}
                >
                  <CardContent className="flex items-start gap-3 p-4">
                    <div className={cn(
                      'p-2 rounded-lg',
                      tipoContrato === tipo.value ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                    )}>
                      {tipo.icon}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{tipo.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{tipo.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Step 1: Método de Medição */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Selecione o método de medição:</p>
              {metodos.map(m => (
                <Card
                  key={m.value}
                  className={cn(
                    'cursor-pointer transition-all hover:border-primary/50',
                    effectiveMedicao === m.value && 'border-primary ring-1 ring-primary/30 bg-primary/5'
                  )}
                  onClick={() => setTipoMedicao(m.value)}
                >
                  <CardContent className="p-4">
                    <p className="text-sm font-medium">{m.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{m.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Step 2: Granularidade (read-only) */}
          {step === 2 && granularidade && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Granularidade de medição determinada automaticamente:</p>
              <Card className="border-primary ring-1 ring-primary/30 bg-primary/5">
                <CardContent className="p-4 flex items-start gap-3">
                  <Info className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{granularidade.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{granularidade.description}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: Confirmação */}
          {step === 3 && tipoContrato && effectiveMedicao && granularidade && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Revise as configurações antes de prosseguir:</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <span className="text-sm text-muted-foreground">Tipo de Contrato</span>
                  <Badge variant="outline">{TIPOS_CONTRATO.find(t => t.value === tipoContrato)?.label}</Badge>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <span className="text-sm text-muted-foreground">Método de Medição</span>
                  <Badge variant="outline">{metodos.find(m => m.value === effectiveMedicao)?.label ?? effectiveMedicao}</Badge>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">Granularidade</span>
                  <Badge variant="outline">{granularidade.label}</Badge>
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 flex items-start gap-2">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Estas configurações serão aplicadas ao contrato e determinarão quais templates e fluxos de medição estarão disponíveis.
                  O tipo pode ser alterado enquanto o contrato estiver em rascunho.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="ghost" onClick={handleBack} disabled={step === 0}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Voltar
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => handleOpenChange(false)}>Cancelar</Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={handleNext} disabled={!canNext()}>
                Avançar <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleConfirm} disabled={!canNext()}>
                <Check className="h-4 w-4 mr-1" /> Confirmar
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
